import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight, Check, Home, Building2, Sun, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: "Property Type", progress: 25 },
  { id: 2, title: "Property Height", progress: 50 },
  { id: 3, title: "Number of Panels", progress: 75 },
  { id: 4, title: "Contact Information", progress: 100 },
];

const PROPERTY_TYPES = [
  { id: "residential", label: "Residential", icon: Home, description: "Single family homes" },
  { id: "commercial", label: "Commercial", icon: Building2, description: "Business properties" },
  { id: "solar-farm", label: "Solar Farm", icon: Sun, description: "Large scale installations" },
];

const PROPERTY_HEIGHTS = [
  { id: "1-story", label: "1 Story" },
  { id: "2-story", label: "2 Story" },
  { id: "3-story", label: "3 Story" },
];

const SUBMISSION_FAILED_MESSAGE =
  "We were unable to submit your request. Please try again or call us at (903) 780-6957.";
const SUCCESS_HEADING = "Information Received!";
const SUCCESS_BODY_LINES = [
  "Thank you for contacting Solar Pros Cleaning Services.",
  "Your quote request has been received successfully.",
  "We will review your request and contact you soon.",
];

function formatPhoneNumber(value) {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const EMPTY = {
  propertyType: "", propertyHeight: "", numPanels: "",
  firstName: "", lastName: "", streetAddress: "", addressLine2: "",
  city: "", state: "", postalCode: "", country: "United States",
  phone: "", email: "", website: "",
};

export default function QuoteModal({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});

  const inputBase = "w-full p-3 rounded-lg border-2 focus:outline-none focus:border-[#1E5EDB] transition-colors";

  const validateStep = () => {
    const e = {};
    if (currentStep === 1 && !formData.propertyType) e.propertyType = "Please select a property type";
    if (currentStep === 2 && !formData.propertyHeight) e.propertyHeight = "Please select property height";
    if (currentStep === 3) {
      if (!formData.numPanels) e.numPanels = "Please enter number of panels";
      else if (parseInt(formData.numPanels) < 1) e.numPanels = "Must be at least 1 panel";
    }
    if (currentStep === 4) {
      if (!formData.firstName) e.firstName = "First name is required";
      if (!formData.lastName) e.lastName = "Last name is required";
      if (!formData.streetAddress) e.streetAddress = "Street address is required";
      if (!formData.city) e.city = "City is required";
      if (!formData.state) e.state = "State is required";
      if (!formData.postalCode) e.postalCode = "Postal code is required";
      if (!formData.phone) e.phone = "Phone is required";
      else if (formData.phone.replace(/\D/g, "").length < 10) e.phone = "Please enter a valid 10-digit phone number";
      if (!formData.email) e.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = "Please enter a valid email";
    }
    setFieldErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < 4) setCurrentStep(currentStep + 1);
    else handleSubmit();
  };

  const handleBack = () => {
    if (currentStep > 1) { setCurrentStep(currentStep - 1); setFieldErrors({}); }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/quotes`, {
        property_type: formData.propertyType,
        property_height: formData.propertyHeight,
        num_panels: parseInt(formData.numPanels),
        first_name: formData.firstName,
        last_name: formData.lastName,
        street_address: formData.streetAddress,
        address_line_2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
      });
      setIsSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message;
      setError(typeof msg === "string" ? msg : SUBMISSION_FAILED_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(EMPTY);
    setFieldErrors({});
    setIsSuccess(false);
    setError("");
    onClose();
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  if (!isOpen) return null;
  const step = STEPS.find((s) => s.id === currentStep);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 quote-backdrop" data-testid="quote-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden animate-fade-in-up flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-2xl font-extrabold text-[#0A2342]">Start Your Quote</h2>
          <button onClick={handleClose} className="p-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="quote-modal-close" aria-label="Close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Progress */}
        {!isSuccess && (
          <div className="px-6 py-4 bg-[#F4F7FB]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#0A2342]">{step.title}</span>
              <span className="text-sm text-slate-500">{step.progress}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#1E5EDB] rounded-full transition-all duration-500 ease-out" style={{ width: `${step.progress}%` }} data-testid="quote-progress-bar" />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto">
          {isSuccess ? (
            <div className="text-center py-8" data-testid="quote-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-display text-2xl font-extrabold text-[#0A2342] mb-2">{SUCCESS_HEADING}</h3>
              <div className="text-slate-600 space-y-1">
                {SUCCESS_BODY_LINES.map((line, i) => <p key={i}>{line}</p>)}
              </div>
            </div>
          ) : (
            <>
              {currentStep === 1 && (
                <div className="space-y-4" data-testid="step-property-type">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Property Type <span className="text-red-500">*</span></label>
                  {PROPERTY_TYPES.map((t) => {
                    const active = formData.propertyType === t.label;
                    return (
                      <button key={t.id} onClick={() => updateField("propertyType", t.label)} data-testid={`property-type-${t.id}`}
                        className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${active ? "border-[#1E5EDB] bg-[#EAF1FB]" : "border-slate-200 hover:border-[#9DBBF0]"}`}>
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${active ? "bg-[#1E5EDB] text-white" : "bg-slate-100 text-slate-600"}`}>
                          <t.icon className="w-6 h-6" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-[#0A2342]">{t.label}</p>
                          <p className="text-sm text-slate-500">{t.description}</p>
                        </div>
                        {active && <Check className="w-5 h-5 text-[#1E5EDB] ml-auto" />}
                      </button>
                    );
                  })}
                  {fieldErrors.propertyType && <p className="text-red-500 text-sm mt-2">{fieldErrors.propertyType}</p>}
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-4" data-testid="step-property-height">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Height of Property <span className="text-red-500">*</span></label>
                  {PROPERTY_HEIGHTS.map((h) => {
                    const active = formData.propertyHeight === h.label;
                    return (
                      <button key={h.id} onClick={() => updateField("propertyHeight", h.label)} data-testid={`property-height-${h.id}`}
                        className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${active ? "border-[#1E5EDB] bg-[#EAF1FB]" : "border-slate-200 hover:border-[#9DBBF0]"}`}>
                        <span className="font-medium text-[#0A2342]">{h.label}</span>
                        {active && <Check className="w-5 h-5 text-[#1E5EDB]" />}
                      </button>
                    );
                  })}
                  {fieldErrors.propertyHeight && <p className="text-red-500 text-sm mt-2">{fieldErrors.propertyHeight}</p>}
                </div>
              )}

              {currentStep === 3 && (
                <div data-testid="step-num-panels">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Number of Panels <span className="text-red-500">*</span></label>
                  <input type="number" min="1" value={formData.numPanels} onChange={(e) => updateField("numPanels", e.target.value)}
                    placeholder="Enter number of panels" data-testid="num-panels-input"
                    className={`${inputBase} ${fieldErrors.numPanels ? "border-red-500" : "border-slate-200"}`} />
                  {fieldErrors.numPanels && <p className="text-red-500 text-sm mt-2">{fieldErrors.numPanels}</p>}
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-4" data-testid="step-contact-info">
                  <div style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }} aria-hidden="true">
                    <label>Website (leave blank)
                      <input type="text" tabIndex="-1" autoComplete="off" value={formData.website} onChange={(e) => updateField("website", e.target.value)} />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">First Name <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.firstName} onChange={(e) => updateField("firstName", e.target.value)} data-testid="first-name-input"
                        className={`${inputBase} ${fieldErrors.firstName ? "border-red-500" : "border-slate-200"}`} />
                      {fieldErrors.firstName && <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.lastName} onChange={(e) => updateField("lastName", e.target.value)} data-testid="last-name-input"
                        className={`${inputBase} ${fieldErrors.lastName ? "border-red-500" : "border-slate-200"}`} />
                      {fieldErrors.lastName && <p className="text-red-500 text-xs mt-1">{fieldErrors.lastName}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Street Address <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.streetAddress} onChange={(e) => updateField("streetAddress", e.target.value)} data-testid="street-address-input"
                      className={`${inputBase} ${fieldErrors.streetAddress ? "border-red-500" : "border-slate-200"}`} />
                    {fieldErrors.streetAddress && <p className="text-red-500 text-xs mt-1">{fieldErrors.streetAddress}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                    <input type="text" value={formData.addressLine2} onChange={(e) => updateField("addressLine2", e.target.value)} data-testid="address-line-2-input"
                      className={`${inputBase} border-slate-200`} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.city} onChange={(e) => updateField("city", e.target.value)} data-testid="city-input"
                        className={`${inputBase} ${fieldErrors.city ? "border-red-500" : "border-slate-200"}`} />
                      {fieldErrors.city && <p className="text-red-500 text-xs mt-1">{fieldErrors.city}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.state} onChange={(e) => updateField("state", e.target.value)} data-testid="state-input"
                        className={`${inputBase} ${fieldErrors.state ? "border-red-500" : "border-slate-200"}`} />
                      {fieldErrors.state && <p className="text-red-500 text-xs mt-1">{fieldErrors.state}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Postal Code <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.postalCode} onChange={(e) => updateField("postalCode", e.target.value)} data-testid="postal-code-input"
                        className={`${inputBase} ${fieldErrors.postalCode ? "border-red-500" : "border-slate-200"}`} />
                      {fieldErrors.postalCode && <p className="text-red-500 text-xs mt-1">{fieldErrors.postalCode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                      <input type="text" value={formData.country} onChange={(e) => updateField("country", e.target.value)} data-testid="country-input"
                        className={`${inputBase} border-slate-200`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" value={formData.phone} onChange={(e) => updateField("phone", formatPhoneNumber(e.target.value))}
                      placeholder="(555) 123-4567" data-testid="phone-input"
                      className={`${inputBase} ${fieldErrors.phone ? "border-red-500" : "border-slate-200"}`} />
                    {fieldErrors.phone && <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <input type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} data-testid="email-input"
                      className={`${inputBase} ${fieldErrors.email ? "border-red-500" : "border-slate-200"}`} />
                    {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="submission-error">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-[#F4F7FB]">
          {isSuccess ? (
            <button onClick={handleClose} data-testid="quote-done-btn"
              className="w-full px-6 py-3 bg-[#0A2342] hover:bg-[#0B2A5B] text-white font-display font-bold rounded-full transition-colors">
              Done
            </button>
          ) : (
            <>
              <button onClick={handleBack} disabled={currentStep === 1 || isSubmitting} data-testid="quote-back-btn"
                className={`px-6 py-3 rounded-full font-medium transition-colors flex items-center gap-2 ${currentStep === 1 || isSubmitting ? "text-slate-300 cursor-not-allowed" : "text-[#0A2342] hover:bg-slate-200"}`}>
                <ChevronLeft className="w-5 h-5" /> Back
              </button>
              <button onClick={handleNext} disabled={isSubmitting} data-testid="quote-next-btn"
                className="px-8 py-3 bg-[#1E5EDB] hover:bg-[#1A52C2] text-white font-display font-bold rounded-full transition-colors flex items-center gap-2 disabled:opacity-50">
                {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>)
                  : currentStep === 4 ? "Submit" : (<>Next <ChevronRight className="w-5 h-5" /></>)}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
